"use client";

import { useRef, useState } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { PAYPAL_CUSTOM_PAYMENT_METHOD_ID } from "~/lib/payment-constants";
import { Button } from "~/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import {
  createPayPalOrderAction,
  createRelationshipPayPalOrderAction,
  createTherapistReportPayPalOrderAction,
} from "~/server/actions/paypal-actions";
import { api } from "~/trpc/react";
import { EUR } from "~/lib/currency";
import type { PaymentMetadata } from "~/types/payment";
import { PaymentErrorState } from "./payment-error-state";
import { usePaymentElementStatus } from "./use-payment-element-status";

export interface PaymentBillingDetails {
  name?: string;
  email?: string;
}

export interface PaymentFormProps {
  clientSecret: string;
  amount?: number;
  onSuccess: () => void;
  onError: (error: string) => void;
  returnUrl: string;
  paymentMetadata?: PaymentMetadata;
  billingDetails?: PaymentBillingDetails;
  submitLabel?: string;
}

export function PaymentForm({
  amount,
  onSuccess,
  onError,
  returnUrl,
  paymentMetadata,
  billingDetails,
  submitLabel,
}: Omit<PaymentFormProps, "clientSecret">) {
  const validateSessionPaymentIntent =
    api.paymentIntent.validateSessionPaymentIntent.useMutation();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const submissionInFlight = useRef(false);
  const {
    handleLoadError,
    handleReady,
    status: elementStatus,
  } = usePaymentElementStatus();

  const createPayPalOrder = useMutation({
    mutationFn: createPayPalOrderAction,
  });
  const createRelationshipPayPalOrder = useMutation({
    mutationFn: createRelationshipPayPalOrderAction,
  });
  const createTherapistReportPayPalOrder = useMutation({
    mutationFn: createTherapistReportPayPalOrderAction,
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || elementStatus !== "ready") {
      onError(
        "Sistem plaćanja nije spreman. Osveži stranicu i pokušaj ponovo.",
      );
      return;
    }

    if (submissionInFlight.current) {
      return;
    }

    submissionInFlight.current = true;
    setIsProcessing(true);
    let redirectStarted = false;

    try {
      const { error: submitError, selectedPaymentMethod } =
        await elements.submit();

      if (submitError) {
        const errorMessage =
          submitError.message ?? "Došlo je do greške prilikom obrade plaćanja";
        onError(errorMessage);
        return;
      }

      if (selectedPaymentMethod === PAYPAL_CUSTOM_PAYMENT_METHOD_ID) {
        if (!paymentMetadata) {
          onError("Podaci o plaćanju nedostaju. Pokušaj ponovo.");
          return;
        }

        try {
          const returnUrlWithParams = new URL(
            returnUrl,
            window.location.origin,
          );
          if (!paymentMetadata.therapistReportCheckoutId) {
            returnUrlWithParams.searchParams.set("step", "3");
            returnUrlWithParams.searchParams.set(
              "sessionQuantity",
              paymentMetadata.sessionQuantity.toString(),
            );
            returnUrlWithParams.searchParams.set(
              "therapistId",
              paymentMetadata.therapistId,
            );
            returnUrlWithParams.searchParams.set(
              "therapyType",
              paymentMetadata.therapyType,
            );
            if (paymentMetadata.sessionTimestamp) {
              returnUrlWithParams.searchParams.set(
                "sessionTimestamp",
                paymentMetadata.sessionTimestamp,
              );
            }
            returnUrlWithParams.searchParams.set(
              "cv",
              paymentMetadata.conversionValue.toString(),
            );
            if (paymentMetadata.trackingEventName) {
              returnUrlWithParams.searchParams.set(
                "te",
                paymentMetadata.trackingEventName,
              );
            }
            if (paymentMetadata.proposalMessageId != null) {
              returnUrlWithParams.searchParams.set(
                "proposalId",
                paymentMetadata.proposalMessageId.toString(),
              );
            }
          }

          const cancelUrlClean = new URL(window.location.href);
          cancelUrlClean.searchParams.delete("payment_intent");
          cancelUrlClean.searchParams.delete("payment_intent_client_secret");
          cancelUrlClean.searchParams.delete("redirect_status");
          cancelUrlClean.searchParams.delete("token");
          cancelUrlClean.searchParams.delete("PayerID");

          const result = paymentMetadata.therapistReportCheckoutId
            ? await createTherapistReportPayPalOrder.mutateAsync({
                checkoutId: paymentMetadata.therapistReportCheckoutId,
                returnUrl: returnUrlWithParams.toString(),
                cancelUrl: cancelUrlClean.toString(),
              })
            : paymentMetadata.therapyRelationshipId
              ? await createRelationshipPayPalOrder.mutateAsync({
                  therapyRelationshipId: paymentMetadata.therapyRelationshipId,
                  sessionTimestamp: paymentMetadata.sessionTimestamp,
                  sessionQuantity: paymentMetadata.sessionQuantity,
                  attributionBridgeId: paymentMetadata.attributionBridgeId,
                  proposalMessageId: paymentMetadata.proposalMessageId,
                  returnUrl: returnUrlWithParams.toString(),
                  cancelUrl: cancelUrlClean.toString(),
                })
              : await createPayPalOrder.mutateAsync({
                  therapistId: paymentMetadata.therapistId,
                  therapyType: paymentMetadata.therapyType,
                  sessionTimestamp: paymentMetadata.sessionTimestamp,
                  sessionQuantity: paymentMetadata.sessionQuantity,
                  attributionBridgeId: paymentMetadata.attributionBridgeId,
                  proposalMessageId: paymentMetadata.proposalMessageId,
                  returnUrl: returnUrlWithParams.toString(),
                  cancelUrl: cancelUrlClean.toString(),
                });

          // Use window.location.href for external redirect to PayPal
          // This ensures clean state and prevents React state caching issues
          window.location.href = result.approvalUrl;
          redirectStarted = true;
        } catch (err) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : "Neuspešno kreiranje PayPal plaćanja";
          onError(errorMessage);
        }
        return;
      }

      const sanitizedBillingDetails = {
        name: billingDetails?.name?.trim() || undefined,
        email: billingDetails?.email?.trim() || undefined,
      };

      if (paymentMetadata?.sessionTimestamp) {
        if (!paymentMetadata.paymentId) {
          throw new Error(
            "Podaci o plaćanju nedostaju. Osveži stranicu i pokušaj ponovo.",
          );
        }
        await validateSessionPaymentIntent.mutateAsync({
          paymentIntentId: paymentMetadata.paymentId,
        });
      }
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          ...(sanitizedBillingDetails.email
            ? { receipt_email: sanitizedBillingDetails.email }
            : {}),
          ...(sanitizedBillingDetails.name || sanitizedBillingDetails.email
            ? {
                payment_method_data: {
                  billing_details: sanitizedBillingDetails,
                },
              }
            : {}),
        },
      });

      if (confirmError) {
        let errorMessage = "Došlo je do greške prilikom plaćanja";
        if (confirmError.type === "card_error") {
          errorMessage =
            confirmError.message ??
            "Kartica je odbijena. Pokušaj sa drugom karticom.";
        } else if (confirmError.type === "validation_error") {
          errorMessage =
            "Podaci o plaćanju nisu validni. Proveri unete podatke.";
        } else if (confirmError.message) {
          errorMessage = confirmError.message;
        }
        onError(errorMessage);
      } else {
        onSuccess();
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Došlo je do neočekivane greške prilikom plaćanja";
      onError(errorMessage);
    } finally {
      if (!redirectStarted) {
        submissionInFlight.current = false;
        setIsProcessing(false);
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8"
      data-testid="payment-form"
    >
      <PaymentElement
        onLoadError={handleLoadError}
        onReady={handleReady}
        options={{
          layout: {
            type: "tabs",
            defaultCollapsed: false,
          },
          wallets: {
            link: "never",
          },
          ...(billingDetails?.name || billingDetails?.email
            ? {
                defaultValues: {
                  billingDetails: {
                    name: billingDetails.name ?? "",
                    email: billingDetails.email ?? "",
                  },
                },
              }
            : {}),
          paymentMethodOrder: [
            "card",
            PAYPAL_CUSTOM_PAYMENT_METHOD_ID,
            "google_pay",
            "apple_pay",
          ],
        }}
      />
      {elementStatus === "error" ? (
        <PaymentErrorState
          title="Plaćanje trenutno nije dostupno"
          errorMessage="Polja za plaćanje nisu učitana. Proveri internet vezu ili podešavanja browsera, pa ponovo učitaj stranicu."
          helperText="Plaćanje nije pokrenuto i ništa nije naplaćeno."
          actionLabel="Ponovo učitaj stranicu"
          onAction={() => window.location.reload()}
        />
      ) : null}
      <Button
        type="submit"
        disabled={!stripe || elementStatus !== "ready" || isProcessing}
        isLoading={isProcessing}
        variant="cta"
        size="lg"
        className="w-full"
        data-testid="payment-submit-button"
      >
        {isProcessing
          ? "Procesiranje..."
          : (submitLabel ??
            `Plati ${EUR.format(amount ?? paymentMetadata?.amount ?? 0)}`)}
      </Button>
    </form>
  );
}
